import { useEffect, type ReactNode } from 'react';

/**
 * The drawer shell: a container, one or more drawers docked to its edges, and
 * the content they sit beside.
 *
 * Two modes:
 *   - `side`: the drawer is a flex item and takes its width out of the content.
 *   - `over`: the drawer floats above the content on top of a backdrop.
 *
 * Layout lives in `styles/_ui-primitives.scss` rather than in utilities here.
 * The drawer's contract is more than a box: `over` mode slides in on a
 * transform, the drawer and the content sit at fixed flex `order`s so a
 * left and a right drawer can share one container, and a closed drawer
 * collapses to zero width while staying mounted. Feature stylesheets also
 * reach into these hooks - the bookmarks panel sizes `.cw-drawer__inner`, and
 * styles.scss re-positions `.bookmarks-panel.cw-drawer--side` on narrow
 * screens. Restating any of that in utilities would mean two sources of truth
 * for one behaviour.
 */

export type SidenavMode = 'side' | 'over';
export type SidenavPosition = 'start' | 'end';

export interface SidenavContainerProps {
  id?: string;
  className?: string;
  children: ReactNode;
}

export function SidenavContainer({ id, className, children }: SidenavContainerProps) {
  return (
    <div id={id} className={['cw-drawer-container', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

export interface SidenavProps {
  /** `side` docks the drawer beside the content; `over` floats it above. */
  mode?: SidenavMode;
  /** Which edge it is anchored to. `end` is the right-hand assistant panel. */
  position?: SidenavPosition;
  opened: boolean;
  onClose?: () => void;
  /**
   * Pins the drawer to the viewport instead of the container. Paired with
   * `fixedTopGap` this is how the overlay drawers clear the 40px toolbar.
   */
  fixedInViewport?: boolean;
  fixedTopGap?: number;
  /** Suppresses the scrim in `over` mode - the bookmarks panel opens without one. */
  disableBackdrop?: boolean;
  className?: string;
  children: ReactNode;
}

export function Sidenav({
  mode = 'side',
  position = 'start',
  opened,
  onClose,
  fixedInViewport = false,
  fixedTopGap = 0,
  disableBackdrop = false,
  className,
  children
}: SidenavProps) {
  // Escape closes an overlay drawer.
  useEffect(() => {
    if (mode !== 'over' || !opened || !onClose) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mode, opened, onClose]);

  const classes = [
    'cw-drawer',
    mode === 'side' ? 'cw-drawer--side' : 'cw-drawer--over',
    position === 'end' ? 'cw-drawer--end' : 'cw-drawer--start',
    opened ? 'cw-drawer--open' : 'cw-drawer--closed',
    fixedInViewport ? 'cw-drawer--fixed' : null,
    className
  ]
    .filter(Boolean)
    .join(' ');

  const style = fixedInViewport ? { top: `${fixedTopGap}px` } : undefined;

  return (
    <>
      {mode === 'over' && opened && !disableBackdrop && (
        <div className="cw-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      )}

      {/*
        Kept mounted when closed rather than unmounted, so the drawers keep
        their scroll position and their open/close transition - and so the
        assistant does not throw away its conversation every time the panel is
        dismissed. `inert` takes the closed drawer out of the tab order, which
        is the same pairing the nav sublists use.
      */}
      <div className={classes} aria-hidden={!opened} inert={!opened}>
        <div className="cw-drawer__inner">{children}</div>
      </div>
    </>
  );
}

export interface SidenavContentProps {
  className?: string;
  children: ReactNode;
}

export function SidenavContent({ className, children }: SidenavContentProps) {
  return (
    <div className={['cw-drawer-content', className].filter(Boolean).join(' ')}>{children}</div>
  );
}
